import styled from "styled-components";
import {selectCaret} from "components/selectCaret.styles";
import {fontFamilies, radii} from "theme/tokens";

export const Section = styled.section`
	/*
	 * The container the subtitle and both tables measure (kitecorp/transferify-ui#4018): its width is the
	 * frame's, border included, and the subtitle sits outside the frame, so the section is the one ancestor both
	 * can ask.
	 */
	container: comparison / inline-size;
	margin-top: 48px;
	min-width: 0;
	text-align: left;

	@media (max-width: 480px) {
		margin-top: 40px;
	}
`;

export const Title = styled.h2`
	margin: 0 0 4px;
	font-weight: 700;
	font-size: 22px;
	line-height: 1.25;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const Subtitle = styled.p`
	margin: 0 0 18px;
	font-size: 14px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Which scroller the comparison's plan headers stick to.
 *
 * - `ancestor`: the ModalSheet body (the pricing dialog).
 * - `page`: the page scroller, so the table reads like the rest of the page and its headers stick against the
 *   page scroll (owner, ui#2426).
 *
 * Neither scrolls the table sideways: where the four plans do not fit, the section shows two (ui#4018).
 */
export type ComparisonScroll = "ancestor" | "page";

/**
 * The section width below which the comparison shows the two-plan table and its own subtitle
 * (kitecorp/transferify-ui#4018). Not a width the four-plan table needs (it has no minimum width): it is the
 * owner's line, a `/pricing` page below 961px wide, where the four columns used to scroll sideways, which takes
 * in tablets such as a Surface Pro 10 in portrait (960px), carried over to the section. Where the scrollbar
 * overlays the page (phones, macOS by default) a 961px page leaves the section 917px, the page's 22px side
 * padding off each side. Where the browser draws a classic scrollbar (Windows, most Linux desktops), the page
 * scroller gives it about 15px, so the line moves to a window about 976px wide; the dialog's line moves the same
 * way with its sheet body's scrollbar. Either side of the line the table never scrolls sideways.
 */
const TWO_PLAN_LINE = "917px";
const TWO_PLAN_COMPARISON = `comparison (width < ${TWO_PLAN_LINE})`;

/** The subtitle of the four-plan view. */
export const FourPlanSubtitle = styled.span`
	@container ${TWO_PLAN_COMPARISON} {
		display: none;
	}
`;

/** The subtitle of the two-plan view, in place of the four-plan one, switched with the tables. */
export const TwoPlanSubtitle = styled.span`
	display: none;

	@container ${TWO_PLAN_COMPARISON} {
		display: inline;
	}
`;

export const Frame = styled.div<{$scroll: ComparisonScroll}>`
	--comparison-feature-column-width: 14rem;
	/*
	 * Sticky column headers stick to the top of whatever scroll container owns this table. In ancestor mode
	 * (the pricing dialog) that container is the ModalSheet body, whose top padding would otherwise leave the
	 * sticky header floating that far below the viewport edge and expose a strip of scrolled rows above it.
	 * Cancel that padding so the header sits flush. The page scroller has no top padding (the nav is absolute
	 * and scrolls away with the page), so the offset is zero.
	 */
	--comparison-sticky-top-offset: ${({$scroll}) => $scroll === "ancestor"
		? "calc(-1 * var(--sheet-body-padding-block-start, 0px))"
		: "0px"};
	position: relative;
	isolation: isolate;
	width: 100%;
	min-width: 0;
	max-width: 100%;
	/* No drop shadow (owner, ui#870): the 1px border is what separates the table from the page. */
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.panel};
	background: ${({theme}) => theme.tokens.card};
`;

const Table = styled.table`
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;
	table-layout: fixed;

	th,
	td {
		box-sizing: border-box;
		padding: 16px;
		text-align: center;
		font-size: 14.5px;
		line-height: 1.4;
		border-top: 1px solid ${({theme}) => theme.tokens.line};
	}

	th + th,
	td + td {
		border-left: 1px solid ${({theme}) => theme.tokens.line};
	}

	thead th {
		border-top: none;
		border-bottom: 1px solid ${({theme}) => theme.tokens.cardBorder};
		padding: 18px 16px;
		vertical-align: middle;
	}

	tbody tr:first-child > * {
		border-top: none;
	}
`;

/** Every plan side by side, shown wherever the section is at least `TWO_PLAN_LINE` wide. */
export const FourPlanTable = styled(Table)`
	@container ${TWO_PLAN_COMPARISON} {
		display: none;
	}
`;

/** A phone's comparison: tighter cells, so the two plan columns keep their room beside the feature names. */
const PHONE_COMPARISON = "comparison (width < 30rem)";

/** A comparison too narrow for a plan's name at full size beside the picker's caret (a 320px phone, the dialog on a phone). */
const COMPACT_COMPARISON = "comparison (width < 24rem)";

/** The narrowest comparison, the pricing dialog on a 320px phone: the smallest type and insets that keep "Premium" whole. */
const TINY_COMPARISON = "comparison (width < 17rem)";

/**
 * Two plans side by side, each chosen in a picker, shown wherever the section is narrower than `TWO_PLAN_LINE`.
 * The feature column takes about a third of the comparison's width, within bounds, so each plan column keeps room
 * for its picker on the narrowest phone. A third of the comparison's width (`cqi`), not of the table (`%`): a table cell's width that mixes a
 * percentage into `clamp()` is read as no width at all, which split the table into three equal columns.
 */
export const TwoPlanTable = styled(Table)`
	--comparison-feature-column-width: clamp(6.75rem, 32cqi, 14rem);
	display: none;

	@container ${TWO_PLAN_COMPARISON} {
		display: table;
	}

	th,
	td {
		/*
		 * A word wider than its column (a German compound) breaks inside the cell instead of running under the
		 * next: hyphenated where the browser knows the page's language, anywhere otherwise. Only a word of ten
		 * letters or more is hyphenated, where the browser supports that limit, so a short word such as "expired"
		 * moves to the next line whole.
		 */
		hyphens: auto;
		hyphenate-limit-chars: 10 4 4;
		overflow-wrap: anywhere;
	}

	/* The picker headings; the corner keeps the feature names' own inset, so its word lines up with theirs. */
	thead th + th {
		padding: 8px 6px;
	}

	@container ${PHONE_COMPARISON} {
		th,
		td {
			padding: 14px 6px;
			font-size: 13.5px;
		}

		thead th {
			padding: 8px 6px;
		}

		thead th + th {
			padding: 8px 4px;
		}
	}

	@container ${COMPACT_COMPARISON} {
		th,
		td {
			padding: 14px 5px;
			font-size: 13px;
		}

		thead th {
			padding: 6px 5px;
		}

		thead th + th {
			padding: 6px 3px;
		}
	}

	@container ${TINY_COMPARISON} {
		--comparison-feature-column-width: 5.5rem;

		th,
		td {
			padding: 12px 4px;
			font-size: 12.5px;
		}

		thead th {
			padding: 6px 4px;
		}

		thead th + th {
			padding: 6px 2px;
		}
	}
`;

export const FeatureHeading = styled.th`
	width: var(--comparison-feature-column-width);
	box-sizing: border-box;
	text-align: left !important;
	vertical-align: middle;
	font-weight: 650;
	color: ${({theme}) => theme.tokens.ink2};
	background: ${({theme}) => theme.tokens.card};
	/* Its own right border marks where the feature names end and the plan columns begin. */
	border-right: 1px solid ${({theme}) => theme.tokens.cardBorder};

	thead & {
		position: sticky;
		top: var(--comparison-sticky-top-offset, 0px);
		z-index: 4;
		font-weight: 700;
		color: ${({theme}) => theme.tokens.ink};
	}

	tbody tr:nth-child(even) & {
		background: ${({theme}) => theme.tokens.card2};
	}

	@media (hover: hover) {
		tbody tr:hover & {
			color: ${({theme}) => theme.tokens.ink};
			background: ${({theme}) => theme.tokens.field};
		}
	}
`;

export const ColumnHeading = styled.th<{$highlight: boolean}>`
	position: sticky;
	top: var(--comparison-sticky-top-offset, 0px);
	z-index: 3;
	background-color: ${({theme}) => theme.tokens.card};
	background-image: ${({theme, $highlight}) => $highlight
		? `linear-gradient(${theme.tokens.accentTint}, ${theme.tokens.accentTint})`
		: "none"};
	/*
	 * The blue cap only. The column's side edges are already drawn by the table's own 1px separators,
	 * so the inset side rings this used to carry landed a second line right beside each of them —
	 * the doubled border ui#854 reported. The accent tint is what marks the column.
	 */
	box-shadow: ${({theme, $highlight}) => $highlight ? `inset 0 3px ${theme.tokens.blue}` : "none"};
`;

/**
 * A plan picker's box: the plan's name and a caret (`selectCaret`), with Plus's blue edge on the one showing
 * Plus.
 */
export const PickerShell = styled.div<{$highlight: boolean}>`
	${selectCaret};
	--select-caret-inset: 11px;
	--select-caret-size: 7px;
	--select-caret-stroke: 2px;
	--select-caret-color: ${({theme, $highlight}) => $highlight ? theme.tokens.blue : theme.tokens.ink2};
	display: flex;
	align-items: center;
	min-width: 0;
	min-height: 44px;
	box-sizing: border-box;
	padding: 0 26px 0 10px;
	border: 1px solid ${({theme, $highlight}) => $highlight ? theme.tokens.blue : theme.tokens.fieldLine};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink};

	&:has(select:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	@container ${COMPACT_COMPARISON} {
		--select-caret-inset: 6px;
		--select-caret-size: 6px;
		padding: 0 17px 0 6px;
	}

	@container ${TINY_COMPARISON} {
		--select-caret-inset: 4px;
		--select-caret-size: 5px;
		padding: 0 13px 0 4px;
	}
`;

/** The plan's name as the box shows it; the select over it is what is read out and pressed. */
export const PickerFace = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: ${fontFamilies.sans};
	font-size: 16px;
	font-weight: 700;
	line-height: 1.2;

	@container ${COMPACT_COMPARISON} {
		font-size: 13.5px;
	}

	@container ${TINY_COMPARISON} {
		font-size: 12.5px;
	}
`;

/**
 * The native select, laid transparently over the whole box: a press anywhere on the box opens the platform's own
 * picker. Its type stays at 16px while the face under it shrinks, because iOS zooms the page into any form
 * field set smaller when it takes focus.
 */
export const Picker = styled.select`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	margin: 0;
	opacity: 0;
	font-size: 16px;
	cursor: pointer;
	appearance: none;
`;

export const Cell = styled.td<{$highlight: boolean}>`
	background-color: ${({theme}) => theme.tokens.card};
	background-image: ${({theme, $highlight}) => $highlight
		? `linear-gradient(${theme.tokens.accentTint}, ${theme.tokens.accentTint})`
		: "none"};

	tbody tr:nth-child(even) & {
		background-color: ${({theme}) => theme.tokens.card2};
	}

	@media (hover: hover) {
		tbody tr:hover & {
			background-color: ${({theme}) => theme.tokens.field};
		}
	}
`;

export const ColumnName = styled.span`
	display: block;
	font-weight: 700;
	font-size: 17px;
	line-height: 1.2;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const ColumnPrice = styled.span`
	display: block;
	min-height: 1.4em;
	margin-top: 5px;
	font-family: ${fontFamilies.mono};
	font-size: 13px;
	font-weight: 700;
	line-height: 1.4;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const Yes = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: ${radii.pill};
	background-color: ${({theme}) => theme.tokens.card};
	background-image: ${({theme}) => `linear-gradient(${theme.tokens.signalTint}, ${theme.tokens.signalTint})`};
	color: ${({theme}) => theme.tokens.ink};
	font-weight: 700;
	font-size: 16px;
	line-height: 1;
`;

export const No = styled.span`
	display: inline-block;
	min-width: 24px;
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 18px;
	line-height: 1;
`;

export const TextValue = styled.span`
	font-family: ${fontFamilies.mono};
	font-weight: 700;
	font-size: 13.5px;
	line-height: 1.45;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink};
`;

/** Qualifications that a boolean cell cannot carry, under the table rather than inside a cell. */
export const Footnotes = styled.ul`
	margin: 14px 0 0;
	padding: 0;
	list-style: none;
	display: grid;
	gap: 6px;
`;

export const Footnote = styled.li`
	font-size: 13px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
	/* Hanging marker keeps a wrapped note aligned under its own text, not under the bullet. */
	padding-inline-start: 14px;
	text-indent: -14px;

	&::before {
		content: "•";
		display: inline-block;
		width: 14px;
		text-indent: 0;
		color: ${({theme}) => theme.tokens.ink2};
	}
`;
